import { tx as translateInterfaceText } from '@/utils/i18n';
/**
 * googleDriveService
 * ------------------
 * Integração com Google Drive via Google Identity Services (GIS) para o OAuth
 * + Google Picker para a seleção de arquivos + Drive REST API para o download.
 * Não usa o SDK `gapi` legado para auth (apenas o loader do Picker).
 *
 * IMPORTANTE — escopo `drive.file` (não `drive.readonly`):
 *   Usamos o escopo NÃO-restrito `https://www.googleapis.com/auth/drive.file`,
 *   que concede acesso APENAS aos arquivos que o usuário escolhe explicitamente
 *   no Google Picker. Isso dispensa o security assessment (CASA) exigido para
 *   escopos restritos como `drive.readonly` e simplifica a verificação do app
 *   no Google Cloud Console.
 *
 *   Consequência: NÃO é possível listar o Drive inteiro (files.list). A seleção
 *   passa obrigatoriamente pelo Picker — o usuário navega/busca na UI oficial do
 *   Google e os arquivos selecionados ficam acessíveis via Drive API com o token.
 *
 * Fluxo:
 *   1. loadScripts() — injeta accounts.google.com/gsi/client uma única vez
 *   2. requestAccessToken() — popup OAuth, devolve access_token (TTL 1h)
 *   3. pickVideoFiles(token) — abre o Google Picker e devolve os arquivos escolhidos
 *   4. downloadFile(token, file) — GET /drive/v3/files/{id}?alt=media → Blob
 *
 * Token vive em memória apenas (não persiste). User reautentica a cada sessão.
 *
 * Requer no .env:
 *   VITE_GOOGLE_CLIENT_ID       — OAuth Client ID do Google Cloud Console
 *   VITE_GOOGLE_DEVELOPER_KEY   — API Key (Picker API) do Google Cloud Console
 */

const GIS_SCRIPT_URL = 'https://accounts.google.com/gsi/client';
const PICKER_SCRIPT_URL = 'https://apis.google.com/js/api.js';
const DRIVE_API_BASE = 'https://www.googleapis.com/drive/v3';
const SCOPE = 'https://www.googleapis.com/auth/drive.file';

// Mimetypes de vídeo aceitos no Picker (restringe a seleção apenas a vídeos).
const VIDEO_MIME_TYPES =
  'video/mp4,video/quicktime,video/x-msvideo,video/x-matroska,video/webm,video/mpeg,video/3gpp,video/x-flv,video/ogg,video/x-ms-wmv';

const CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined;
const DEVELOPER_KEY = import.meta.env.VITE_GOOGLE_DEVELOPER_KEY as string | undefined;

// O número do projeto (App ID do Picker) é o prefixo do Client ID, antes do
// primeiro '-'. Necessário para o Picker associar os arquivos selecionados ao
// app e garantir o acesso via drive.file.
const APP_ID = CLIENT_ID ? CLIENT_ID.split('-')[0] : undefined;

export interface DriveFile {
  id: string;
  name: string;
  size: number;
  mimeType: string;
  modifiedTime: string;
  thumbnailLink?: string;
  iconLink?: string;
}

export interface DriveUser {
  email: string;
  name?: string;
  picture?: string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type TokenClient = any;

let scriptsLoadedPromise: Promise<void> | null = null;
let pickerLoadedPromise: Promise<void> | null = null;
let tokenClientCache: TokenClient | null = null;

// Injeta um <script> de forma idempotente e resolve quando carrega.
function injectScript(src: string): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    if (typeof document === 'undefined') {
      reject(new Error(translateInterfaceText("Document indisponível (SSR?)")));
      return;
    }

    const existing = document.querySelector<HTMLScriptElement>(`script[src="${src}"]`);
    if (existing) {
      if (existing.dataset.loaded === 'true') {
        resolve();
      } else {
        existing.addEventListener('load', () => resolve(), { once: true });
        existing.addEventListener('error', () => reject(new Error(translateInterfaceText("Falha ao carregar {value0}", { value0: (src) }))), { once: true });
      }
      return;
    }

    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      script.dataset.loaded = 'true';
      resolve();
    };
    script.onerror = () => reject(new Error(translateInterfaceText("Falha ao carregar {value0}", { value0: (src) })));
    document.head.appendChild(script);
  });
}

// Carrega o script GIS (auth) uma única vez. Idempotente.
async function loadScripts(): Promise<void> {
  if (scriptsLoadedPromise) return scriptsLoadedPromise;
  scriptsLoadedPromise = injectScript(GIS_SCRIPT_URL);
  return scriptsLoadedPromise;
}

// Carrega a Google Picker API (api.js + gapi.load('picker')) uma única vez.
async function loadPicker(): Promise<void> {
  if (pickerLoadedPromise) return pickerLoadedPromise;

  pickerLoadedPromise = injectScript(PICKER_SCRIPT_URL).then(
    () =>
      new Promise<void>((resolve, reject) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const gapi = (window as any).gapi;
        if (!gapi) {
          reject(new Error(translateInterfaceText("gapi indisponível após carregar api.js")));
          return;
        }
        gapi.load('picker', {
          callback: () => resolve(),
          onerror: () => reject(new Error(translateInterfaceText("Falha ao carregar o módulo Picker"))),
        });
      }),
  );

  return pickerLoadedPromise;
}

// Solicita access_token via popup OAuth. Devolve quando o user autoriza ou
// rejeita. Reutiliza o tokenClient pra próximas chamadas.
export async function requestAccessToken(): Promise<string> {
  if (!CLIENT_ID) {
    throw new Error(
      translateInterfaceText("VITE_GOOGLE_CLIENT_ID não configurado. Adicione no .env e reinicie o dev server.")
    );
  }

  await loadScripts();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const google = (window as any).google;
  if (!google?.accounts?.oauth2) {
    throw new Error(translateInterfaceText("Google Identity Services não disponível"));
  }

  return new Promise<string>((resolve, reject) => {
    try {
      const client = google.accounts.oauth2.initTokenClient({
        client_id: CLIENT_ID,
        scope: SCOPE,
        callback: (response: { access_token?: string; error?: string; error_description?: string }) => {
          if (response.error) {
            reject(new Error(response.error_description || response.error));
            return;
          }
          if (!response.access_token) {
            reject(new Error(translateInterfaceText("Token vazio retornado pelo Google")));
            return;
          }
          resolve(response.access_token);
        },
        error_callback: (err: { type?: string; message?: string }) => {
          // Eventos comuns: popup_closed, popup_failed_to_open, unknown
          reject(new Error(err.message || err.type || translateInterfaceText("OAuth cancelado")));
        },
      });
      tokenClientCache = client;
      // prompt='' = pula tela de consentimento se já autorizou antes
      client.requestAccessToken({ prompt: '' });
    } catch (err) {
      reject(err);
    }
  });
}

// Revoga o token e força nova autorização na próxima chamada.
export async function revokeToken(token: string): Promise<void> {
  await loadScripts();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const google = (window as any).google;
  if (google?.accounts?.oauth2?.revoke) {
    return new Promise<void>(resolve => {
      google.accounts.oauth2.revoke(token, () => resolve());
    });
  }
}

// Converte um documento do Picker no nosso formato DriveFile.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapPickerDoc(doc: any): DriveFile {
  const thumbnails = Array.isArray(doc.thumbnails) ? doc.thumbnails : [];
  // lastEditedUtc vem em milissegundos (epoch).
  const modified = doc.lastEditedUtc ? new Date(Number(doc.lastEditedUtc)) : new Date();
  return {
    id: doc.id,
    name: doc.name,
    size: Number(doc.sizeBytes || 0),
    mimeType: doc.mimeType || 'video/mp4',
    modifiedTime: modified.toISOString(),
    thumbnailLink: thumbnails.length ? thumbnails[thumbnails.length - 1].url : undefined,
    iconLink: doc.iconUrl,
  };
}

// Abre o Google Picker (view de vídeos) e devolve os arquivos selecionados.
// Resolve com [] se o usuário cancelar. Com o escopo drive.file, é a seleção
// no Picker que concede ao app o acesso a cada arquivo.
export async function pickVideoFiles(token: string): Promise<DriveFile[]> {
  if (!DEVELOPER_KEY) {
    throw new Error(
      translateInterfaceText("VITE_GOOGLE_DEVELOPER_KEY não configurado. Adicione no .env e reinicie o dev server.")
    );
  }

  await loadPicker();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const google = (window as any).google;
  if (!google?.picker) {
    throw new Error(translateInterfaceText("Google Picker não disponível"));
  }

  return new Promise<DriveFile[]>((resolve, reject) => {
    try {
      // Só vídeos do próprio usuário: setOwnedByMe(true) exclui "Compartilhados
      // comigo" (itens de terceiros); sem pastas e restrito aos mimetypes de vídeo.
      const view = new google.picker.DocsView(google.picker.ViewId.DOCS_VIDEOS)
        .setOwnedByMe(true)
        .setIncludeFolders(false)
        .setMimeTypes(VIDEO_MIME_TYPES);
      // Modo grade: miniaturas maiores. Se o Drive conseguir gerar o preview, ele
      // aparece aqui; com drive.file, alguns vídeos ainda caem em ícone genérico.
      if (google.picker.DocsViewMode?.GRID) {
        view.setMode(google.picker.DocsViewMode.GRID);
      }

      const builder = new google.picker.PickerBuilder()
        .setOAuthToken(token)
        .setDeveloperKey(DEVELOPER_KEY)
        .addView(view)
        .enableFeature(google.picker.Feature.MULTISELECT_ENABLED)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .setCallback((data: any) => {
          const action = data[google.picker.Response.ACTION];
          if (action === google.picker.Action.PICKED) {
            const docs = data[google.picker.Response.DOCUMENTS] || [];
            resolve(docs.map(mapPickerDoc));
          } else if (action === google.picker.Action.CANCEL) {
            resolve([]);
          }
        });

      // setAppId melhora a confiabilidade do acesso drive.file aos selecionados.
      if (APP_ID) builder.setAppId(APP_ID);

      builder.build().setVisible(true);
    } catch (err) {
      reject(err);
    }
  });
}

// Busca dados do user autenticado (email pra mostrar "Conectado como ...").
// Usa a userinfo endpoint do OAuth2 — o GIS concede openid implicitamente.
export async function getUserInfo(token: string): Promise<DriveUser> {
  const res = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    // Não é fatal — se falhar, retorna fallback
    return { email: 'conta Google' };
  }
  const data = await res.json();
  return {
    email: data.email || 'conta Google',
    name: data.name,
    picture: data.picture,
  };
}

// Baixa o conteúdo binário de um arquivo do Drive e empacota em File com o
// nome original. onProgress recebe (loaded, total) em bytes durante o stream.
export async function downloadFile(
  token: string,
  file: DriveFile,
  onProgress?: (loaded: number, total: number) => void,
): Promise<File> {
  const res = await fetch(`${DRIVE_API_BASE}/files/${file.id}?alt=media`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(translateInterfaceText("Falha ao baixar {value0}: HTTP {value1}{value2}", { value0: (file.name), value1: (res.status), value2: (detail ? ` — ${detail.slice(0, 200)}` : '') }));
  }

  // Stream com progresso. Se o body não suportar reader, cai pro blob direto.
  if (!res.body || !onProgress) {
    const blob = await res.blob();
    return new File([blob], file.name, { type: file.mimeType || 'video/mp4' });
  }

  const reader = res.body.getReader();
  const total = file.size || Number(res.headers.get('content-length') || 0);
  const chunks: Uint8Array[] = [];
  let loaded = 0;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    if (value) {
      chunks.push(value);
      loaded += value.length;
      onProgress(loaded, total);
    }
  }

  const blob = new Blob(chunks as BlobPart[], { type: file.mimeType || 'video/mp4' });
  return new File([blob], file.name, { type: file.mimeType || 'video/mp4' });
}

// Helper pra checagem rápida no UI antes mesmo de tentar conectar.
// Precisa do Client ID (OAuth) e da Developer Key (Picker).
export function isGoogleDriveConfigured(): boolean {
  return !!CLIENT_ID && !!DEVELOPER_KEY;
}
